import {
  Activity,
  Bike,
  BicepsFlexed,
  Dumbbell,
  Footprints,
  Waves,
  type LucideIcon,
} from "lucide-react";
import type { ManualExerciseRecord } from "../services/ManualExerciseService";
import type { ExerciseLogEntry } from "./exerciseLogHistory";

export const EXERCISE_TYPES = [
  { id: "running", label: "Running", icon: Footprints, metPerMin: 9 },
  { id: "walking", label: "Walking", icon: Activity, metPerMin: 4 },
  { id: "cycling", label: "Cycling", icon: Bike, metPerMin: 7 },
  { id: "swimming", label: "Swimming", icon: Waves, metPerMin: 8 },
  { id: "gym", label: "Gym", icon: Dumbbell, metPerMin: 5 },
  { id: "other", label: "Other", icon: BicepsFlexed, metPerMin: 5 },
] as const;

export const INTENSITY_LEVELS = [
  { id: "low" as const, label: "Low", multiplier: 0.7 },
  { id: "medium" as const, label: "Medium", multiplier: 1.0 },
  { id: "high" as const, label: "High", multiplier: 1.4 },
];

export const DEFAULT_EXERCISE_TYPE = EXERCISE_TYPES[0].id;
export const DEFAULT_INTENSITY = "medium" as const;

export const exerciseTheme = {
  gradient:
    "from-orange-500/[0.08] via-orange-500/[0.03] to-transparent dark:from-orange-500/[0.14]",
  iconWrap:
    "border border-orange-200/80 bg-orange-50 dark:border-orange-800/50 dark:bg-orange-950/40",
  icon: "text-orange-600 dark:text-orange-400",
  tileActive:
    "border-2 border-orange-500 bg-orange-50 shadow-sm ring-1 ring-orange-500/25 dark:border-orange-500 dark:bg-orange-950/35",
  tileIconActive: "bg-orange-500 text-white",
  tileTextActive: "text-orange-700 dark:text-orange-400",
  inputFocus: "focus:ring-orange-400/50 focus:border-orange-400",
  estimateBox:
    "border border-orange-200/80 bg-orange-50 dark:border-orange-800/45 dark:bg-orange-950/30",
  estimateText: "text-orange-700 dark:text-orange-400",
  loggedIcon: "bg-orange-500 text-white",
  loggedRow:
    "border border-orange-200/70 bg-orange-50/90 dark:border-orange-800/40 dark:bg-orange-950/25",
  cardGradient: "from-orange-500/[0.07] to-transparent",
} as const;

export const INTENSITY_THEME = {
  low: {
    active: "border-transparent bg-emerald-600 text-white shadow-sm dark:bg-emerald-600",
  },
  medium: {
    active: "border-transparent bg-orange-500 text-white shadow-sm dark:bg-orange-500",
  },
  high: {
    active: "border-transparent bg-red-600 text-white shadow-sm dark:bg-red-600",
  },
} as const;

export function getTypeMeta(typeId: string) {
  return EXERCISE_TYPES.find((t) => t.id === typeId) ?? EXERCISE_TYPES[0];
}

export function getLocalDateString(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export function mapApiRecordToEntry(record: ManualExerciseRecord): ExerciseLogEntry {
  const durationMin =
    (record.duration_hours ?? 0) * 60 + (record.duration_min ?? 0);
  return {
    id:
      record.id ??
      `${record.exercise_name}-${record.logged_at ?? Date.now()}`,
    name: record.exercise_name,
    duration: String(durationMin),
    calories: String(record.calories_kcal),
    type: record.exercise_type,
    intensity: record.intensity,
    date: record.date_local ?? getLocalDateString(),
    loggedAt: record.logged_at ?? new Date().toISOString(),
  };
}
